import React, { useMemo, useState } from 'react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { api } from '../../../lib/api';
import { useToast } from '../../../components/ui/Toast';
import { Button, Card, Badge } from '../../../components/ui/Kit';
import Modal from '../../../components/ui/Modal';
import SafeIcon from '../../../common/SafeIcon';
import { calculateGoalProgress } from '../../../lib/financialAnalysisUtils';

const GOAL_CATEGORIES = [
  'Emergency Fund',
  'Retirement',
  'Home Purchase',
  'Education',
  'Travel',
  'Investment',
  'Other',
];

const PRIORITIES = [
  { value: 'high', label: 'High', variant: 'danger' },
  { value: 'medium', label: 'Medium', variant: 'warning' },
  { value: 'low', label: 'Low', variant: 'default' },
];

const PRIORITY_ORDER = { high: 0, medium: 1, low: 2 };

const cellInput =
  'w-full h-9 rounded-md border border-gray-200 bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent';

function formatCurrency(val) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(Number(val) || 0);
}

function goalPayloadFromRow(row) {
  return {
    title: row.title || 'Untitled goal',
    description: row.description || '',
    category: row.category || 'Other',
    priority: row.priority || 'medium',
    target_amount: Number(row.target_amount) || 0,
    current_amount: Number(row.current_amount) || 0,
    target_date: row.target_date || null,
  };
}

function priorityBadgeVariant(priority) {
  return PRIORITIES.find((p) => p.value === priority)?.variant || 'default';
}

export default function GoalsTab({
  analysisId,
  agencyId,
  financialGoals,
  setFinancialGoals,
  scheduleRowSave,
  setSaveStatus,
  markSavedBriefly,
  goalsRef,
}) {
  const { addToast } = useToast();
  const [adding, setAdding] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const sortedGoals = useMemo(() => {
    return [...(financialGoals || [])].sort((a, b) => {
      const pa = PRIORITY_ORDER[a.priority] ?? 9;
      const pb = PRIORITY_ORDER[b.priority] ?? 9;
      if (pa !== pb) return pa - pb;
      return (a.title || '').localeCompare(b.title || '');
    });
  }, [financialGoals]);

  const onGoalChange = (rowId, field, value) => {
    setFinancialGoals((prev) => {
      const next = prev.map((r) => (r.id === rowId ? { ...r, [field]: value } : r));
      goalsRef.current = next;
      return next;
    });
    scheduleRowSave('goals', rowId, () => {
      const row = (goalsRef.current || []).find((r) => r.id === rowId);
      return goalPayloadFromRow(row || {});
    });
  };

  const addGoal = async () => {
    setAdding(true);
    try {
      const row = await api.financialAnalysis.financialGoals.create(analysisId, {
        agency_id: agencyId,
        title: 'New goal',
        description: '',
        category: 'Other',
        priority: 'medium',
        target_amount: 0,
        current_amount: 0,
        target_date: null,
      });
      setFinancialGoals((prev) => {
        const next = [...prev, row];
        goalsRef.current = next;
        return next;
      });
      markSavedBriefly();
    } catch (err) {
      addToast({ type: 'error', title: 'Could not add goal', message: err?.message });
    } finally {
      setAdding(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.financialAnalysis.financialGoals.delete(deleteTarget.id);
      setFinancialGoals((prev) => {
        const next = prev.filter((r) => r.id !== deleteTarget.id);
        goalsRef.current = next;
        return next;
      });
      setSaveStatus('saved');
      markSavedBriefly();
      setDeleteTarget(null);
    } catch (err) {
      addToast({ type: 'error', title: 'Could not delete goal', message: err?.message });
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-semibold text-gray-900">Financial Goals</h2>
          <p className="text-xs text-gray-500 mt-0.5">Sorted by priority (high first)</p>
        </div>
        <Button size="sm" variant="outline" onClick={addGoal} disabled={adding} isLoading={adding}>
          <SafeIcon icon={FiPlus} className="w-4 h-4 mr-1" />
          Add Goal
        </Button>
      </div>

      {sortedGoals.length === 0 ? (
        <Card className="p-10 text-center">
          <p className="text-sm text-gray-500">No goals yet. Add one to start tracking progress.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {sortedGoals.map((goal) => {
            const progress = calculateGoalProgress(goal);
            return (
              <Card key={goal.id} className="p-4 border border-gray-200 hover:border-gray-300 transition-colors">
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div className="flex items-center gap-2 flex-wrap min-w-0">
                    <Badge variant={priorityBadgeVariant(goal.priority)}>
                      {(goal.priority || 'medium').toUpperCase()}
                    </Badge>
                    <span className="text-xs text-gray-500">{goal.category || 'Other'}</span>
                  </div>
                  <button
                    type="button"
                    className="p-1.5 text-gray-400 hover:text-red-600 transition-colors flex-shrink-0"
                    onClick={() => setDeleteTarget(goal)}
                    aria-label="Delete goal"
                  >
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                </div>

                <div className="space-y-2.5">
                  <input
                    className={`${cellInput} font-medium`}
                    value={goal.title || ''}
                    placeholder="Goal title"
                    onChange={(e) => onGoalChange(goal.id, 'title', e.target.value)}
                  />
                  <textarea
                    className="w-full min-h-[56px] rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-y"
                    value={goal.description || ''}
                    placeholder="Description (optional)"
                    onChange={(e) => onGoalChange(goal.id, 'description', e.target.value)}
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-[10px] font-medium uppercase tracking-wide text-gray-400 mb-1">
                        Category
                      </label>
                      <select
                        className={cellInput}
                        value={goal.category || 'Other'}
                        onChange={(e) => onGoalChange(goal.id, 'category', e.target.value)}
                      >
                        {GOAL_CATEGORIES.map((c) => (
                          <option key={c} value={c}>{c}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium uppercase tracking-wide text-gray-400 mb-1">
                        Priority
                      </label>
                      <select
                        className={cellInput}
                        value={goal.priority || 'medium'}
                        onChange={(e) => onGoalChange(goal.id, 'priority', e.target.value)}
                      >
                        {PRIORITIES.map((p) => (
                          <option key={p.value} value={p.value}>{p.label}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium uppercase tracking-wide text-gray-400 mb-1">
                        Target Amount
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className={`${cellInput} tabular-nums`}
                        value={goal.target_amount ?? 0}
                        onChange={(e) =>
                          onGoalChange(
                            goal.id,
                            'target_amount',
                            e.target.value === '' ? 0 : Number(e.target.value),
                          )
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-medium uppercase tracking-wide text-gray-400 mb-1">
                        Current Amount
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        className={`${cellInput} tabular-nums`}
                        value={goal.current_amount ?? 0}
                        onChange={(e) =>
                          onGoalChange(
                            goal.id,
                            'current_amount',
                            e.target.value === '' ? 0 : Number(e.target.value),
                          )
                        }
                      />
                    </div>
                    <div className="col-span-2">
                      <label className="block text-[10px] font-medium uppercase tracking-wide text-gray-400 mb-1">
                        Target Date
                      </label>
                      <input
                        type="date"
                        className={cellInput}
                        value={goal.target_date || ''}
                        onChange={(e) => onGoalChange(goal.id, 'target_date', e.target.value || null)}
                      />
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-gray-100">
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="font-medium text-gray-700">
                      {Math.round(progress.progressPct)}% complete
                    </span>
                    <span className="text-gray-500 tabular-nums">
                      {formatCurrency(goal.current_amount)} / {formatCurrency(goal.target_amount)}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary transition-all duration-300"
                      style={{ width: `${Math.min(100, Math.max(0, progress.progressPct))}%` }}
                    />
                  </div>
                  <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-500">
                    <span>
                      {progress.monthsRemaining} month{progress.monthsRemaining === 1 ? '' : 's'} remaining
                    </span>
                    <span className="tabular-nums">
                      {formatCurrency(progress.monthlyRequired)}/month needed
                    </span>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={Boolean(deleteTarget)}
        onClose={() => !deleting && setDeleteTarget(null)}
        title="Delete goal?"
      >
        <p className="text-sm text-gray-500 mb-5">
          Remove{' '}
          <span className="font-medium text-gray-800">{deleteTarget?.title || 'this goal'}</span>?
          This cannot be undone.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={confirmDelete} isLoading={deleting}>
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
}
